import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Shield, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrivacyConsentModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Checkboxes state
  const [checks, setChecks] = useState({
    privacy: false,
    terms: false,
    liability: false,
    suspension: false
  });

  useEffect(() => {
    if (user && user.role === 'client') {
      checkPrivacyConsent();
    }
  }, [user]);

  const checkPrivacyConsent = async () => {
    try {
      // Verificar si ya se saltó en esta sesión
      const skipped = localStorage.getItem('privacy_consent_skipped');
      if (skipped) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API}/me/privacy-consent-status`);
      if (response.data && response.data.consented === false) {
        setOpen(true);
      }
    } catch (error) {
      console.error('Error checking privacy consent:', error);
      // NO bloquear al usuario en caso de error
    } finally {
      setLoading(false);
    }
  };

  const toggleCheck = (key) => {
    setChecks(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const allChecked = checks.privacy && checks.terms && checks.liability && checks.suspension;

  const handleAccept = async () => {
    if (!allChecked) {
      toast.error('Marca todas las casillas para continuar');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/me/accept-privacy-consent`, {
        privacy_accepted: true,
        terms_accepted: true,
        liability_accepted: true,
        suspension_accepted: true,
        accepted_at: new Date().toISOString()
      });
      toast.success('¡Documentos aceptados!');
      setOpen(false);
    } catch (error) {
      toast.error('Error al guardar');
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('privacy_consent_skipped', Date.now().toString());
    setOpen(false);
    toast.info('Puedes aceptar más tarde');
  };

  const openLegalPage = (path) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  if (loading || !user || user.role !== 'client' || !open) return null;

  const documents = [
    { key: 'privacy', label: 'Política de Privacidad', path: '/legal/privacidad', color: 'text-green-400' },
    { key: 'terms', label: 'Términos de Uso', path: '/legal/terminos-uso', color: 'text-blue-400' },
    { key: 'liability', label: 'Exención de Responsabilidad', path: '/legal/exencion-responsabilidad', color: 'text-amber-400' },
    { key: 'suspension', label: 'Política de Bloqueo', path: '/legal/bloqueo-suspension', color: 'text-red-400' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg max-w-md w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 text-center border-b border-zinc-800">
          <div className="mx-auto mb-2 p-3 rounded-full bg-primary/20 w-fit">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <h2 className="text-lg font-bold text-white">Documentos Legales</h2>
          <p className="text-sm text-zinc-400">Acepta para continuar</p>
        </div>

        {/* Documents */}
        <div className="p-4 space-y-3">
          {documents.map((doc) => (
            <div 
              key={doc.key}
              className="flex items-center gap-3 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700"
            >
              {/* Checkbox nativo */}
              <input
                type="checkbox"
                id={`check-${doc.key}`}
                checked={checks[doc.key]}
                onChange={() => toggleCheck(doc.key)}
                className="w-5 h-5 accent-primary cursor-pointer flex-shrink-0"
              />

              {/* Label */}
              <label 
                htmlFor={`check-${doc.key}`}
                className={`flex-1 text-sm font-medium cursor-pointer ${doc.color}`}
              >
                {doc.label}
              </label>

              {/* Link to open document */}
              <button
                type="button"
                onClick={() => openLegalPage(doc.path)}
                className="p-2 rounded hover:bg-zinc-700 text-primary"
              >
                <ExternalLink className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="p-4 border-t border-zinc-800 space-y-2">
          <button
            type="button"
            onClick={handleAccept}
            disabled={!allChecked || submitting}
            className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
              allChecked && !submitting
                ? 'bg-primary text-black hover:bg-primary/90'
                : 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
            }`}
          >
            {submitting ? 'Guardando...' : allChecked ? 'Aceptar y Continuar' : 'Marca todas las casillas'}
          </button>

          <button
            type="button"
            onClick={handleSkip}
            className="w-full py-2 text-sm text-zinc-500 hover:text-zinc-300 underline"
          >
            Revisar más tarde
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyConsentModal;
